/**
 * Shared web + native drag hooks for record table rows (#997).
 */

import { Platform } from 'react-native';

import {
  useNativeDragHandle,
  useRecordTableRowLayout,
  type RecordTableRowBounds,
} from './useNativeTableRowDrag';
import { useWebDragHandle, useWebDropTarget } from './useWebTableRowDrag';

import type { RefObject } from 'react';
import type { View } from 'react-native';

export function useTableRowDragAndDrop({
  enabled,
  rowIndex = 0,
  dragHandleRef,
  dropRowRef,
  onDragHandleStart,
  onDragHandleMove,
  onDragHandleEnd,
  onRowDragOver,
  onRowDrop,
  onRowLayout,
  registerRowRemeasure,
}: {
  enabled: boolean;
  rowIndex?: number;
  dragHandleRef: RefObject<View | null>;
  dropRowRef: RefObject<View | null>;
  onDragHandleStart?: (rowIndex: number) => void;
  onDragHandleMove?: (pageY: number) => void;
  onDragHandleEnd?: () => void;
  onRowDragOver?: (rowIndex: number) => void;
  onRowDrop?: (rowIndex: number) => void;
  onRowLayout?: (rowIndex: number, bounds: RecordTableRowBounds) => void;
  registerRowRemeasure?: (
    index: number,
    remeasure: (() => void) | null,
  ) => void;
}): {
  nativeDragHandleProps: Record<string, unknown>;
  onDropRowLayout: () => void;
} {
  useWebDragHandle(
    dragHandleRef,
    enabled,
    rowIndex,
    index => onDragHandleStart?.(index),
    () => onDragHandleEnd?.(),
  );

  useWebDropTarget(
    dropRowRef,
    enabled,
    rowIndex,
    index => onRowDragOver?.(index),
    index => onRowDrop?.(index),
  );

  const nativeDragHandleProps = useNativeDragHandle(
    enabled,
    rowIndex,
    index => onDragHandleStart?.(index),
    pageY => onDragHandleMove?.(pageY),
    () => onDragHandleEnd?.(),
  );

  const onDropRowLayout = useRecordTableRowLayout(
    dropRowRef,
    enabled,
    rowIndex,
    onRowLayout,
    registerRowRemeasure,
  );

  if (Platform.OS === 'web') {
    return { nativeDragHandleProps: {}, onDropRowLayout: () => undefined };
  }

  return { nativeDragHandleProps, onDropRowLayout };
}
